import { createContext, useContext, useMemo, useRef, useState } from "react";
import { Button, Checkbox, Input, Select, message } from "antd";
import { LeftOutlined, SearchOutlined } from "@ant-design/icons";
import type { ColDef, ICellRendererParams } from "ag-grid-enterprise";
import type { AgGridReact } from "ag-grid-react";
import CiqAgGrid from "components/ciq-ag-grid";
import type { DesignScopeListItem } from "mock-data/design-scope";
import {
  DESIGN_SCOPE_FEATURE_TYPE_ID,
  updateDesignScopeInstances
} from "services/design-scope-apis";
import { LodDefaultBadge } from "../components/scope-design-package-row";
import {
  defaultLodIdFromOptions,
  lodOptionHeading,
  type LevelOfDetailOption
} from "../hooks/use-level-of-detail";
import {
  buildMasterformatFilter,
  buildUniformatFilter,
  ClassificationFilterMenu,
  masterformatDivisionId
} from "./classification-filter-menu";
import {
  buildMilestoneLinkUpdates,
  diffMilestoneLinks,
  effectiveLodId,
  initialMilestoneLinks,
  type MilestoneLinkDraft,
  type MilestoneLinkDrafts
} from "./milestone-scope-links";
import { classificationCell, classParts, scopeTitle } from "./scope-list-cells";

export const MANAGE_LINKS_SUCCESS_MESSAGE = "Scope Items successfully updated.";

type LinkRow = {
  id: string;
  title: string;
  uniformatCode: string;
  uniformatTitle: string;
  uniformatCustom: boolean;
  masterformatCode: string;
  masterformatTitle: string;
  masterformatCustom: boolean;
  masterformatDivision: string;
};

function toLinkRow(item: DesignScopeListItem): LinkRow {
  const uniformat = classParts(item, "uni-format");
  const masterformat = classParts(item, "master-format");
  return {
    id: item.id,
    title: scopeTitle(item),
    uniformatCode: uniformat.code,
    uniformatTitle: uniformat.title,
    uniformatCustom: uniformat.custom,
    masterformatCode: masterformat.code,
    masterformatTitle: masterformat.title,
    masterformatCustom: masterformat.custom,
    masterformatDivision: masterformatDivisionId(item)
  };
}

type LodSelectOption = {
  value: string;
  label: string;
  heading: string;
  name: string;
  description: string;
  isDefault: boolean;
};

function toLodSelectOptions(options: LevelOfDetailOption[]): LodSelectOption[] {
  return options.map((opt, n) => {
    const heading = lodOptionHeading(opt, n);
    const name = opt.label.trim();
    return {
      value: opt.id,
      label: name ? `${heading} ${name}` : heading,
      heading,
      name,
      description: opt.description?.trim() ?? "",
      isDefault: Boolean(opt.isDefault)
    };
  });
}

/**
 * Picker state shared with the grid's cell renderers. AG Grid renders React
 * cells inside this tree, so they re-render on change without refreshCells
 * (which would remount the note input and drop focus while typing).
 */
type LinkState = {
  selected: ReadonlySet<string>;
  drafts: MilestoneLinkDrafts;
  defaultLodId: string;
  lodSelectOptions: LodSelectOption[];
  displayedIds: string[];
  disabled: boolean;
  toggle: (id: string, checked: boolean) => void;
  toggleAll: (checked: boolean) => void;
  setDraft: (id: string, patch: Partial<MilestoneLinkDraft>) => void;
};

const LinkStateContext = createContext<LinkState | null>(null);

function useLinkState(): LinkState {
  const state = useContext(LinkStateContext);
  if (!state) throw new Error("LinkStateContext is missing");
  return state;
}

function SelectAllHeader() {
  const { selected, displayedIds, disabled, toggleAll } = useLinkState();
  const count = displayedIds.filter((id) => selected.has(id)).length;
  const all = displayedIds.length > 0 && count === displayedIds.length;
  return (
    <Checkbox
      aria-label="Select all scope items"
      data-testid="manage-links-select-all"
      checked={all}
      indeterminate={count > 0 && !all}
      disabled={disabled || displayedIds.length === 0}
      onChange={(e) => toggleAll(e.target.checked)}
    />
  );
}

function SelectCell({ data }: ICellRendererParams<LinkRow>) {
  const { selected, disabled, toggle } = useLinkState();
  if (!data) return null;
  return (
    <Checkbox
      aria-label={`Select ${data.title}`}
      data-testid={`manage-links-select-${data.id}`}
      checked={selected.has(data.id)}
      disabled={disabled}
      onChange={(e) => toggle(data.id, e.target.checked)}
    />
  );
}

function LodCell({ data }: ICellRendererParams<LinkRow>) {
  const {
    selected,
    drafts,
    defaultLodId,
    lodSelectOptions,
    disabled,
    setDraft
  } = useLinkState();
  if (!data) return null;
  if (!selected.has(data.id)) return <span>-</span>;

  const value = effectiveLodId(drafts[data.id], defaultLodId);
  // Keep a saved LOD that is no longer in the project list selectable.
  const options =
    !value || lodSelectOptions.some((o) => o.value === value)
      ? lodSelectOptions
      : [
          ...lodSelectOptions,
          {
            value,
            label: value,
            heading: value,
            name: "",
            description: "",
            isDefault: false
          }
        ];
  const current = options.find((o) => o.value === value);

  return (
    <Select<string, LodSelectOption>
      data-testid={`manage-links-lod-${data.id}`}
      className="w-full"
      classNames={{ popup: { root: "lod-select-dropdown" } }}
      styles={{ popup: { root: { minWidth: 320 } } }}
      placeholder="Select LOD"
      virtual={false}
      disabled={disabled}
      value={value || undefined}
      options={options}
      popupRender={(menu) => (
        <div>
          <div className="px-3 pb-1 pt-2 text-sm text-ciq-black text-opacity-88">
            Select Level of Detail (LOD)
          </div>
          {menu}
        </div>
      )}
      labelRender={() => (
        <span className="block truncate">{current?.label}</span>
      )}
      optionRender={(option) => {
        const opt = option.data;
        return (
          <span className="flex flex-col gap-0.5 py-0.5">
            <span className="flex min-w-0 items-center gap-2">
              <span className="text-sm font-semibold text-ciq-black text-opacity-88">
                {opt.heading} {opt.name}
              </span>
              {opt.isDefault ? <LodDefaultBadge /> : null}
            </span>
            {opt.description ? (
              <span
                className="whitespace-normal text-xs leading-4"
                style={{ color: "var(--ciq-black-a45)" }}
              >
                {opt.description}
              </span>
            ) : null}
          </span>
        );
      }}
      onChange={(lodId) => setDraft(data.id, { lodId })}
    />
  );
}

function NoteCell({ data }: ICellRendererParams<LinkRow>) {
  const { selected, drafts, disabled, setDraft } = useLinkState();
  if (!data) return null;
  if (!selected.has(data.id)) return <span>-</span>;
  return (
    <Input
      data-testid={`manage-links-note-${data.id}`}
      placeholder="Add Notes"
      disabled={disabled}
      value={drafts[data.id]?.notes ?? ""}
      onChange={(e) => setDraft(data.id, { notes: e.target.value })}
    />
  );
}

/** Let the LOD select and note input own their keystrokes. */
const ownKeyboard = () => true;

const COLUMN_DEFS: ColDef<LinkRow>[] = [
  {
    colId: "select",
    headerName: "",
    headerComponent: SelectAllHeader,
    cellRenderer: SelectCell,
    width: 52,
    flex: 0,
    pinned: "left",
    sortable: false,
    resizable: false,
    suppressHeaderMenuButton: true
  },
  {
    colId: "title",
    field: "title",
    headerName: "Title",
    minWidth: 180,
    flex: 1.2
  },
  {
    colId: "uniformat",
    field: "uniformatCode",
    headerName: "UniFormat",
    minWidth: 190,
    flex: 1.3,
    cellRenderer: (p: { data?: LinkRow }) =>
      classificationCell(
        "UF",
        p.data?.uniformatCode ?? "",
        p.data?.uniformatTitle ?? "",
        p.data?.uniformatCustom
      )
  },
  {
    colId: "masterformat",
    field: "masterformatCode",
    headerName: "MasterFormat",
    minWidth: 190,
    flex: 1.3,
    cellRenderer: (p: { data?: LinkRow }) =>
      classificationCell(
        "MF",
        p.data?.masterformatCode ?? "",
        p.data?.masterformatTitle ?? "",
        p.data?.masterformatCustom
      )
  },
  {
    colId: "lod",
    headerName: "Select LOD",
    minWidth: 200,
    flex: 1.3,
    sortable: false,
    suppressKeyboardEvent: ownKeyboard,
    cellRenderer: LodCell
  },
  {
    colId: "note",
    headerName: "Note",
    minWidth: 200,
    flex: 1.3,
    sortable: false,
    suppressKeyboardEvent: ownKeyboard,
    cellRenderer: NoteCell
  }
];

export type ManageScopeLinksPanelProps = {
  /** Every scope item in the project (report-engine `logs/10`). */
  items: DesignScopeListItem[];
  designPackageId: string;
  milestoneId: string;
  designPackageTitle?: string;
  milestoneName?: string;
  lodOptions: LevelOfDetailOption[];
  token: string | null | undefined;
  /** Back / Cancel: leave without saving. */
  onBack: () => void;
  /** Called after a successful save. */
  onSaved: () => void | Promise<void>;
};

/**
 * "Manage Scope Items Links": pick which project scope items are linked to a
 * design package milestone, with an LOD and note per link. Changes stay local
 * until Save, which sends one bulk `/features/update` for the changed items.
 */
export function ManageScopeLinksPanel({
  items,
  designPackageId,
  milestoneId,
  designPackageTitle,
  milestoneName,
  lodOptions,
  token,
  onBack,
  onSaved
}: ManageScopeLinksPanelProps) {
  const gridRef = useRef<AgGridReact<LinkRow>>(null);

  // Snapshot of the saved links when the picker opened; the diff baseline.
  const [initial] = useState(() =>
    initialMilestoneLinks(items, designPackageId, milestoneId)
  );
  const [selected, setSelected] = useState<ReadonlySet<string>>(
    () => new Set(Object.keys(initial))
  );
  const [drafts, setDrafts] = useState<MilestoneLinkDrafts>(() => ({
    ...initial
  }));
  const [query, setQuery] = useState("");
  const [uniformat, setUniformat] = useState<string[]>([]);
  const [masterformat, setMasterformat] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const defaultLodId = useMemo(
    () => defaultLodIdFromOptions(lodOptions),
    [lodOptions]
  );
  const lodSelectOptions = useMemo(
    () => toLodSelectOptions(lodOptions),
    [lodOptions]
  );

  const rows = useMemo(() => items.map(toLinkRow), [items]);
  const uniformatFilter = useMemo(() => buildUniformatFilter(items), [items]);
  const masterformatFilter = useMemo(
    () => buildMasterformatFilter(items, "DIVISIONS IN THIS PROJECT"),
    [items]
  );

  const displayedRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((row) => {
      if (uniformat.length && !uniformat.includes(row.uniformatCode)) {
        return false;
      }
      if (
        masterformat.length &&
        !masterformat.includes(row.masterformatDivision)
      ) {
        return false;
      }
      return !q || row.title.toLowerCase().includes(q);
    });
  }, [rows, query, uniformat, masterformat]);

  const changes = useMemo(
    () => diffMilestoneLinks(initial, selected, drafts, defaultLodId),
    [initial, selected, drafts, defaultLodId]
  );
  const dirty =
    changes.linked.length + changes.unlinked.length + changes.edited.length > 0;

  const linkState = useMemo<LinkState>(() => {
    const seedDraft = (prev: MilestoneLinkDrafts, ids: string[]) => {
      const next = { ...prev };
      for (const id of ids) {
        next[id] ??= { lodId: defaultLodId, notes: "" };
      }
      return next;
    };
    return {
      selected,
      drafts,
      defaultLodId,
      lodSelectOptions,
      displayedIds: displayedRows.map((row) => row.id),
      disabled: saving,
      toggle: (id, checked) => {
        if (checked) setDrafts((prev) => seedDraft(prev, [id]));
        setSelected((prev) => {
          const next = new Set(prev);
          if (checked) next.add(id);
          else next.delete(id);
          return next;
        });
      },
      toggleAll: (checked) => {
        const ids = displayedRows.map((row) => row.id);
        if (checked) setDrafts((prev) => seedDraft(prev, ids));
        setSelected((prev) => {
          const next = new Set(prev);
          for (const id of ids) {
            if (checked) next.add(id);
            else next.delete(id);
          }
          return next;
        });
      },
      setDraft: (id, patch) =>
        setDrafts((prev) => ({
          ...prev,
          [id]: { ...(prev[id] ?? { lodId: "", notes: "" }), ...patch }
        }))
    };
  }, [selected, drafts, defaultLodId, lodSelectOptions, displayedRows, saving]);

  const save = async () => {
    if (!dirty || saving) return;
    if (!token) {
      message.error("Project auth is not ready. Please try again.");
      return;
    }
    const missingLod = [...changes.linked, ...changes.edited].some(
      (id) => !effectiveLodId(drafts[id], defaultLodId)
    );
    if (missingLod) {
      message.error("Select a Level of Detail for every selected scope item.");
      return;
    }

    setSaving(true);
    try {
      const response = await updateDesignScopeInstances(
        {
          feature_type: "DESIGN_SCOPE",
          feature_instance_type_id: DESIGN_SCOPE_FEATURE_TYPE_ID,
          feature_instances: buildMilestoneLinkUpdates({
            items,
            changes,
            drafts,
            designPackageId,
            milestoneId,
            lodOptions,
            defaultLodId
          })
        },
        token
      );
      if (!response.success) {
        message.error(response.errorMsg || "Unable to update scope items.");
        return;
      }
      message.success(MANAGE_LINKS_SUCCESS_MESSAGE);
      await onSaved();
    } catch {
      message.error("Unable to update scope items.");
    } finally {
      setSaving(false);
    }
  };

  const total = rows.length;
  const filtered = displayedRows.length !== total;

  return (
    <div
      className="relative flex h-full min-h-0 flex-col"
      data-testid="manage-links-panel"
      aria-busy={saving}
    >
      <div className="flex items-center gap-3 border-0 border-b border-solid border-ciq-gray-200 px-6 py-4">
        <Button
          icon={<LeftOutlined />}
          aria-label="Back"
          data-testid="manage-links-back"
          disabled={saving}
          onClick={onBack}
        />
        <span className="text-lg font-semibold text-ciq-black-a88">
          Manage Scope Items Links
        </span>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 px-6 pt-4">
        <div>
          {designPackageTitle ? (
            <div className="text-xs font-normal text-ciq-planner-ink4">
              {designPackageTitle}
            </div>
          ) : null}
          {milestoneName ? (
            <div className="text-lg font-semibold text-ciq-black">
              {milestoneName}
            </div>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-ciq-black-a88">
            Select Scope Items to link
          </span>
          <span
            className="rounded-full border border-solid border-ciq-gray-300 px-2 py-0.5 text-xs text-ciq-black-a45"
            data-testid="manage-links-total"
          >
            {filtered ? `${displayedRows.length} of ${total}` : total}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Input
            allowClear
            prefix={<SearchOutlined className="text-black/25" />}
            placeholder="Search Scope Item Title"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="min-w-[200px] max-w-xs flex-1"
            data-testid="manage-links-search"
          />
          <ClassificationFilterMenu
            testId="manage-links-uniformat"
            placeholder="UniFormat"
            allLabel="All UniFormat codes"
            searchPlaceholder="Search UniFormat code or name"
            model={uniformatFilter}
            value={uniformat}
            onChange={setUniformat}
            chipClass="bg-ciq-geekblue-100 text-ciq-geekblue-600"
          />
          <ClassificationFilterMenu
            testId="manage-links-masterformat"
            placeholder="MasterFormat"
            allLabel="All MasterFormat codes"
            searchPlaceholder="Search MasterFormat code or name"
            model={masterformatFilter}
            value={masterformat}
            onChange={setMasterformat}
            showOptionCounts
            chipClass="bg-ciq-golden-purple-100 text-ciq-golden-purple-900"
          />
          <span
            className="ml-auto rounded-full border border-solid border-ciq-primary-950 px-3 py-0.5 text-xs text-ciq-primary-950"
            data-testid="manage-links-selected-count"
          >
            {selected.size} Selected
          </span>
        </div>

        <LinkStateContext.Provider value={linkState}>
          <div className="relative min-h-0 w-full flex-1 [&_.ag-root-wrapper]:h-full [&_.ag-root-wrapper]:w-full [&_.ag-theme-alpine]:h-full [&_.ag-theme-alpine]:w-full">
            <CiqAgGrid<LinkRow>
              gridRef={gridRef}
              rowData={displayedRows}
              columnDefs={COLUMN_DEFS}
              getRowId={(params) => params.data.id}
              sideBar={false}
              disableResizingAutoWidth
              rowSelection={{
                mode: "singleRow",
                checkboxes: false,
                enableClickSelection: false
              }}
              defaultColDef={{
                sortable: true,
                filter: false,
                resizable: true,
                suppressHeaderMenuButton: true,
                flex: 1,
                minWidth: 110
              }}
            />
          </div>
        </LinkStateContext.Provider>
      </div>

      <div className="flex justify-end gap-2 border-0 border-t border-solid border-ciq-gray-200 px-6 py-3">
        <Button
          data-testid="manage-links-cancel"
          disabled={saving}
          onClick={onBack}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          data-testid="manage-links-save"
          disabled={!dirty}
          loading={saving}
          onClick={() => void save()}
        >
          Save
        </Button>
      </div>

      {saving ? (
        <div
          className="absolute inset-0 z-10 cursor-wait bg-white/40"
          data-testid="manage-links-saving-overlay"
        />
      ) : null}
    </div>
  );
}
